import React, { useEffect, useRef, useState } from 'react';
import { get, intersection, difference } from 'lodash';
import axios from 'axios';
import moment from 'moment';
import {
	SdfBox,
	SdfButton,
	SdfExpandableBox,
	SdfIcon,
	SdfSegmentedControl,
	SdfSelect
} from '@synerg/react-components';
import ScopesConsentDetails from './scopeConsentDetails';
import { ModifiyScope } from './subscriptionsDetails/modifyScope';
import { showErrorModal, useApplicationState } from '../../../utilities/applicationState';

export default function ScopesAndAuthorization({ isTabActive, projectDetails, devProjectID, prodProjectID, changeRequestSubmitSuccessData }) {
	const [selectedSegment, setSelectedSegment] = useState(1);
	const [devRequestDateList, setDevRequestDateList] = useState([]);
	const [prodRequestDateList, setProdRequestDateList] = useState([]);
	const [current, setCurrent] = useState(true);
	const [, dispatch] = useApplicationState();

	const [changeRequestsData, setChangeRequestsData] = useState({});
	const [selectedChangeRequest, setSelectedChangeRequest] = useState({});
	const [mergedCanonicals, setMergedCanonicals] = useState([]);
	const [mergedAuthTexts, setMergedAuthTexts] = useState([]);
	const [scopesCount, setScopesCount] = useState(0);
	const [authTextsCount, setAuthTextCount] = useState(0);
	const [isLoadingChangeHistory, setLoadingChangeHistory] = useState(true);
	const [selectedEnvironment, setSelectedEnvironment] = useState('PROD');
	const [selectedRequestDate, setSelectedRequestDate] = useState('');
	const [segments, setSegments] = useState([{ label: 'Dev', value: 0 }, { label: 'Prod', value: 1 }]);

	// modify scope
	const [cancelableRequest, setCancelableRequest] = useState(false);
	const [carDetails, setCarDetails] = useState({});
	const [activeChangeRequest, setActiveChangeRequest] = useState({});
	const [loadModifyScope, setLoadModifyScope] = useState(false);
	const [modifyScopeSuccess, setModifyScopeSuccess] = useState(false);

	const canonicalsTableRef = useRef(null);
	const authTextTableRef = useRef(null);

	const transformSimpleStructure = (list) => {
		const dropDownList = list.map((changerequest, index) => ({
			label: `Req ${changerequest.requestNumber}--${moment.utc(changerequest.createdAt).format('MM-DD-YYYY')}`,
			value: `${changerequest.requestNumber}--${moment.utc(changerequest.createdAt).format('MM-DD-YYYY')}`,
			index
		}));
		dropDownList.unshift({ label: `Current`, value: `current`, index: dropDownList.length });
		return dropDownList;
	};

	const setDevProdDefaults = (devRequestList = [], prodRequestList = []) => {
		const devRequestDateList = transformSimpleStructure(devRequestList);
		const prodRequestDateList = transformSimpleStructure(prodRequestList);
		setDevRequestDateList(devRequestDateList);
		setProdRequestDateList(prodRequestDateList);
		setSelectedRequestDate(prodRequestDateList[0]);
	};

	const setDefaults = (hasProdData, hasDevData) => {
		setSegments(prev => (prev.map(item => ({ ...item, disabled: (item.label === 'Prod' && !hasProdData) || (item.label === 'Dev' && !hasDevData)}))));
		setSelectedSegment(hasProdData ? 1 : 0);
		setSelectedEnvironment(hasProdData ? 'PROD' : 'DEV');
	};

	const getChangeHistory = async () => {
		try {
			// const prodProjectIDString = prodProjectID ? `/${prodProjectID}` : ''
			const prodProjectIDString = prodProjectID ? `${devProjectID}/change-request/${prodProjectID}/history` : `change-request/${devProjectID}/history`;
			const { data: changeRequests } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/project/${prodProjectIDString}`
			);

			const { dev = [], prod = [] } = changeRequests;
			setChangeRequestsData(changeRequests);

			setDevProdDefaults(dev, prod);

		} catch (e) {
			dispatch(showErrorModal(get(e, 'response.data.message', 'Something went wrong when trying to get change history. Please try again.')));
		} finally {
			setLoadingChangeHistory(false);
		}
	};

	const handleEnvironmentSelection = (selection) => {
		setSelectedSegment(selection);
		const environment = selection === 0 ? 'DEV' : 'PROD';
		setSelectedEnvironment(environment);
		setCurrent(true);
		setSelectedRequestDate(environment === 'PROD' ? prodRequestDateList[0] : devRequestDateList[0]);
	};

	const handleRequestDateSelection = (selection) => {
		setCurrent(selection === 'current');
		const listToExtractSelectedItem = selectedEnvironment === 'PROD' ? prodRequestDateList : devRequestDateList;
		const listToExtractChangeRequest = selectedEnvironment === 'PROD' ? changeRequestsData.prod : changeRequestsData.dev;
		const selectedItem = listToExtractSelectedItem.find(item => item.value === selection);
		if (selectedItem) {
			setSelectedRequestDate(selectedItem);
			setSelectedChangeRequest(listToExtractChangeRequest[selectedItem.index]);
		}
	};

	const getMergedCanonicals = () => {
		const existingCanonicals = get(selectedChangeRequest, 'existingAppDetails.canonicals', []);
		const proposedCanonicals = get(selectedChangeRequest, 'proposedAppDetails.canonicals', []);

		const sameCanonicals = intersection(existingCanonicals, proposedCanonicals).map(canonical => {
			return {
				canonicalText: canonical,
				type: 'same'
			};
		});
		const removedCanonicals = difference(existingCanonicals, proposedCanonicals).map(canonical => {
			return {
				canonicalText: canonical,
				type: 'removed'
			};
		});
		const addedCanonicals = difference(proposedCanonicals, existingCanonicals).map(canonical => {
			return {
				canonicalText: canonical,
				type: 'added'
			};
		});

		setScopesCount(sameCanonicals.length + addedCanonicals.length);
		return [...sameCanonicals, ...removedCanonicals, ...addedCanonicals];
	};

	const getMergedAuthText = () => {
		const existingAuthText = get(selectedChangeRequest, 'existingAppDetails.consentTexts', []);
		const proposedAuthText = get(selectedChangeRequest, 'proposedAppDetails.consentTexts', []);

		const sameAuthText = intersection(existingAuthText, proposedAuthText).map(authText => ({
			authText: authText,
			type: 'same'
		}));

		const removedAuthText = difference(existingAuthText, proposedAuthText).map(authText => ({
			authText: authText,
			type: 'removed'
		}));

		const addedAuthText = difference(proposedAuthText, existingAuthText).map(authText => ({
			authText: authText,
			type: 'added'
		}));

		setAuthTextCount(sameAuthText.length + addedAuthText.length);
		return [...sameAuthText, ...removedAuthText, ...addedAuthText];
	};


	const toggleCollapsedMode = (object, isCollapsed, isAuthTexts = false) => {
		object.collapsed = !isCollapsed;
		isAuthTexts ? setMergedAuthTexts([...mergedAuthTexts]) : setMergedCanonicals([...mergedCanonicals]);
	};

	const getCancelableRequestToken = () => {
		if (cancelableRequest) {
			cancelableRequest.cancel('search request canceled');
		}
		const newRequest = axios.CancelToken.source();
		setCancelableRequest(newRequest);
		return newRequest;
	};

	const getActiveCR = async (selectedProjectId) => {
		try {
			setActiveChangeRequest({...activeChangeRequest, [selectedEnvironment]: false});
			const { data: { success = false, changeRequest } } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/project/${selectedProjectId}/change-request/active`
			);

			if (success) {
				setActiveChangeRequest({...activeChangeRequest, [selectedEnvironment]: changeRequest});
			}
		} catch (e) {
			setActiveChangeRequest({...activeChangeRequest, [selectedEnvironment]: false});
		}
	};

	const getCarDetails = async (paginationDetails = {}, environment = 'PROD') => {
		const {
			searchText,
			selectedSubscriptions,
			requestFor
		} = paginationDetails;
		try {
			const params = {
				removeOwnOrgSubs: true,
				projectType: environment,
				search: searchText || null,
				subscriptions: selectedSubscriptions || [],
				type: requestFor || "all"
			};
			const availableProjectID = devProjectID || prodProjectID;
			const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/car-app-details/${availableProjectID}`, {
				params,
				cancelToken: getCancelableRequestToken().token
			});
			setCarDetails(data);
		} catch (e) {
			if (e && e.message !== 'search request canceled') {
			}
		}
	};

	useEffect(() => {
		if (selectedEnvironment) {
			if (selectedEnvironment === 'DEV' && devProjectID) {
				getActiveCR(devProjectID);
			} else if (prodProjectID) {
				getActiveCR(prodProjectID);
			}

			setLoadingChangeHistory(true);
			getChangeHistory();
			getCarDetails({}, selectedEnvironment);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedEnvironment, prodProjectID, devProjectID, modifyScopeSuccess]);

	useEffect(() => {
		setLoadingChangeHistory(true);
		getChangeHistory();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTabActive]);

	useEffect(() => {
		setMergedCanonicals(getMergedCanonicals());
		setMergedAuthTexts(getMergedAuthText());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedChangeRequest]);

	const preAuthRequiredText = get(selectedChangeRequest, 'preAuthRequired', false) === true ? 'Yes' : 'No';
	const appTypeHeaderText = get(selectedChangeRequest, 'applicationType', '') === 'B2B' ? 'Data connector' : 'End-user';

	return (
		<div>
			<div className={"flex pt-10 pb-5 mx-0 justify-between"}>
				<div className="text-heading-05 font-bold">Scope and authorization</div>
				<SdfButton emphasis="primary" onClick={() => setLoadModifyScope(true)}>
					Modify app scope
				</SdfButton>
			</div>

			<SdfBox variant="filled" className="my-5 p-5">
				<div className="flex justify-center">
					<SdfSegmentedControl
						className="border-dark border-solid border-1 border-opacity-25"
						items={segments}
						onSdfChange={({ detail }) => handleEnvironmentSelection(detail)}
						selectedIndex={selectedSegment}
					/>
				</div>
				<div className='flex justify-between py-5 items-end gap-5'>
					<div className="textbox-with-label">
						<SdfSelect
							label={"Status"}
							value={selectedRequestDate}
							items={selectedEnvironment === 'PROD' ? prodRequestDateList : devRequestDateList}
							placeholder="Select..."
							inputWidth={"lg"}
							onSdfChange={({ detail }) => { handleRequestDateSelection(detail.value) }}
						/>
					</div>
					<div className="change-history-details-titles">
						<div className="font-bold mb-2">Environment</div>
						<div className="change-history-data">{current ? 'N/A' : (selectedChangeRequest ? `${selectedChangeRequest.environment}-${selectedChangeRequest.applicationType}` : 'N/A')}</div>
					</div>
					<div className="change-history-details-titles">
						<div className="font-bold mb-2">Pre-auth required?</div>
						<div className="change-history-data">{current ? 'N/A' : preAuthRequiredText}</div>
					</div>
					<div className="change-history-details-titles">
						<div className="font-bold mb-2">Pre-auth cutoff</div>
						<div className="change-history-data">{current ? 'N/A' : (selectedChangeRequest ? moment.utc(selectedChangeRequest.cutOffDate).format('MM/DD/YYYY') : 'N/A')}</div>
					</div>
					<div className="change-history-details-titles">
						<div className="font-bold mb-2">Request added</div>
						<div className="change-history-data">{current ? 'N/A' : (selectedChangeRequest ? moment.utc(selectedChangeRequest.createdAt).format('MM/DD/YYYY') : 'N/A')}</div>
					</div>
					<div className="change-history-details-titles">
						<div className="font-bold mb-2">Requested by</div>
						<div className="change-history-data">{current ? 'N/A' : (selectedChangeRequest && selectedChangeRequest.createdBy ? selectedChangeRequest.createdBy.name : 'N/A')}</div>
					</div>
				</div>
			</SdfBox>
			{!current && !isLoadingChangeHistory && <div>
				<div className="p-1 text-heading-05 font-bold">{`${segments[selectedSegment].label} scope`}</div>
				<SdfExpandableBox
					variant="bordered"
					opened={true}
					triggerPlacement={"after"}
					header={`${appTypeHeaderText} scope (${scopesCount})`}
				>
					<div className="pt-10" ref={canonicalsTableRef} >
						{mergedCanonicals.map(
							(canonicalObject, i) => {
								const hasOverFlowContent = get(canonicalObject, 'hasOverFlowContent', false);
								const collapsedState = get(canonicalObject, 'collapsed', false);
								const type = get(canonicalObject, 'type', 'same');
								return <div key={`canonical-item-${i}`}>
									<div className={`border-0 border-t-1 border-solid border-dark p-5 break-all ${type === 'added' ? 'bg-action-confirm-50' : (type === 'removed' ? 'bg-action-destructive-50' : '')}`}>
										{canonicalObject.canonicalText}
									</div>
									{hasOverFlowContent &&
										<div className='change-history-table-row-toggle'>
											{collapsedState &&
												<SdfIcon icon="action-expand" onClick={() => toggleCollapsedMode(canonicalObject, collapsedState, true)} />}
											{!collapsedState &&
												<SdfIcon icon="action-collapse" onClick={() => toggleCollapsedMode(canonicalObject, collapsedState, true)} />}
										</div>}
								</div>
							})
						}
					</div>
				</SdfExpandableBox>
				<div className="mt-5">
					<div className="p-1 text-heading-05 font-bold">{`${segments[selectedSegment].label} authorization text`}</div>
					<SdfExpandableBox
						variant="bordered"
						opened={true}
						triggerPlacement={"after"}
						header={`${appTypeHeaderText} authorization text (${authTextsCount})`}
					>
						<div className="pt-10" ref={authTextTableRef}>
							{mergedAuthTexts.map(
								(authTextObject, i) => {
									const hasOverFlowContent = get(authTextObject, 'hasOverFlowContent', false);
									const collapsedState = get(authTextObject, 'collapsed', false);
									const type = get(authTextObject, 'type', 'same');
									return <div className="break-word" key={`auth-text-item-${i}`}>
										<div className={`border-0 border-t-1 border-solid border-dark p-5 ${type === 'added' ? 'bg-action-confirm-50' : (type === 'removed' ? 'bg-action-destructive-50' : '')}`}>
											{authTextObject.authText}
										</div>
										{hasOverFlowContent && <div className='change-history-table-row-toggle'>
											{collapsedState && <SdfIcon icon="action-expand" onClick={() => toggleCollapsedMode(authTextObject, collapsedState)} />}
											{!collapsedState &&	<SdfIcon icon="action-collapse" onClick={() => toggleCollapsedMode(authTextObject, collapsedState)} />}
										</div>}
									</div>
								})
							}
						</div>
					</SdfExpandableBox>
				</div>
			</div>}
			<ScopesConsentDetails
				show={current}
				projectDetails={projectDetails}
				setDefaults={setDefaults}
				selectedEnvironment={selectedEnvironment}
				projectCarDetails={carDetails}
			/>
			{loadModifyScope &&
				<ModifiyScope
					projectID={devProjectID}
					prodProjectID={prodProjectID}
					projectDetails={projectDetails}
					loadModifyScope={loadModifyScope}
					setLoadModifyScope={setLoadModifyScope}
					activeChangeRequest={activeChangeRequest}
					carDetails={carDetails}
					setModifyScopeSuccess={setModifyScopeSuccess}
				/>
			}
		</div >
	)
};
